/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../../logging";
import { PlaybackContentType, ThumbnailStatus } from "../enums";
import { Direction, SpeedLevel } from "../trickplay";
import type { TThumbnailEntry } from "../types";
import { BrowserThumbnailStrategy } from "./BrowserThumbnailStrategy";
import {
  collectEvictionTiers,
  computeProtectedWindow,
  selectEvictionVictims
} from "./ThumbnailEvictionPolicy";
import type { TEvictionContext } from "./ThumbnailEvictionPolicy";
import {
  buildThumbnailUrl,
  snapToStep,
  THUMBNAIL_INTERVAL,
  toFrameEpochDomain
} from "./ThumbnailGrid";
import {
  DEFAULT_10FOOT_CONFIG,
  mapToPlaybackCategory,
  resolveDirectionalBatchSize,
  resolveDirectionalBatchSpan,
  resolveEvictionBatchSize,
  resolveGridStride,
  resolveMaxParallelRequests
} from "./ThumbnailManager10FootConfig";
import { clearTimer, ThumbnailSession } from "./ThumbnailSession";
import type {
  PlaybackCategory,
  T10FootThumbnailConfig
} from "./ThumbnailManager10FootConfig";

/**
 * How long a failed offset stays blacklisted from the on-demand lookup path.
 *
 * The dominant failure is a 404 for a frame just past the live edge, which
 * becomes valid within seconds as the CDN publishes it — so the record must
 * expire rather than persist for the whole session.
 */
const FAILED_OFFSET_TTL_MS = 30000;

/** Ceiling on retained failure records so the map cannot grow unbounded. */
const MAX_FAILED_OFFSETS = 200;

/**
 * Standalone thumbnail manager that prefetches and serves thumbnail
 * preview images independently of the playback pipeline.
 *
 * Port of mobi-client-connect-web ThumbnailManager.js to TypeScript
 * with the following changes:
 *   - Regular class instead of singleton (lifecycle managed by caller).
 *   - Strong typing via Map<number, TThumbnailEntry>.
 *   - Simplified public API accepting primitive parameters.
 *   - No optional chaining (SmartTV legacy compatibility).
 */

/**
 * Lifecycle of the 10-foot prefetch engine (PRD §4):
 *
 *   idle ──▶ preload seeded ──▶ directional look-ahead
 *                  ▲                     │
 *                  └─────────────────────┘  (onSeekConfirmed)
 *
 * A direction reversal stays within directional look-ahead (cancel
 * opposite-direction work, recompute, continue).
 *
 * There is deliberately no state FIELD backing this. The only phase any code
 * branches on is "directional look-ahead", which is exactly
 * m10FootActiveSeekDirection !== null — see is10FootTrickPlayActive(). A
 * separate enum was a second source of truth for the same fact, and the two
 * could (and did) desync.
 */

export class ThumbnailManager {
  private static TAG: string = "ThumbnailManager";

  // ── Shared State ─────────────────────────────────────────────────────────

  /**
   * Session token, last-known-good frame and diagnostics counters — shared
   * with the browser strategy so blob accounting and reset invalidation are
   * global to the manager rather than per-strategy.
   */
  private readonly mSession: ThumbnailSession = new ThumbnailSession();

  /**
   * The browser eager-prefetch strategy. Always constructed: it is the
   * fallback whenever the 10-foot path is inactive OR its playback category
   * could not be resolved, which is reachable on a TV too.
   */
  private readonly mBrowser: BrowserThumbnailStrategy =
    new BrowserThumbnailStrategy(this.mSession);

  /**
   * Content start epoch (seconds) or 0 for pure VOD.
   * 10-foot only — the browser strategy keeps its own copy.
   */
  private mStartTime: number = 0;

  /** Content end epoch (seconds) or duration for VOD. 10-foot only. */
  private mEndTime: number = 0;

  /**
   * URL template with {FILENAME} placeholder.
   *
   * Set on BOTH paths, because besides building 10-foot URLs it doubles as the
   * "content parameters have arrived" latch that checkReadyToFetch() gates on.
   */
  private mUrlTemplate: string = "";

  /** Interval between thumbnails (seconds). 10-foot only. */
  private mThumbnailsInterval: number = THUMBNAIL_INTERVAL;

  // ── 10-Foot State ────────────────────────────────────────────────────────

  /** Whether the 10-foot sliding-window strategy is active (TV only). */
  private m10FootIsActive: boolean = false;

  /** Active 10-foot config (merged with defaults on enable). */
  private m10FootConfig: T10FootThumbnailConfig = { ...DEFAULT_10FOOT_CONFIG };

  /**
   * Playback category derived from PlaybackContentType.
   * null means 10-foot path is inactive for the current content.
   */
  private m10FootPlaybackCategory: PlaybackCategory | null = null;

  /** LRU sliding-window cache for 10-foot mode, keyed by time offset. */
  private m10FootCache: Map<number, TThumbnailEntry> = new Map();

  /** In-flight fetch dedup + cancellation map for 10-foot mode. */
  private m10FootInFlight: Map<number, AbortController> = new Map();

  /** Ordered list of time offsets waiting to be fetched in 10-foot mode. */
  private m10FootPendingQueue: number[] = [];

  /**
   * Set of time offsets currently in m10FootPendingQueue.
   * Kept in sync with the queue for O(1) duplicate-check when enqueueing.
   * Prevents the same offset being queued from both the initial preload and a
   * subsequent directional batch before it is drained.
   */
  private m10FootPendingSet: Set<number> = new Set();

  /** Active HTTP request counter (10-foot mode); max = m10FootConfig.MAX_PARALLEL_REQUESTS. */
  private m10FootActiveRequests: number = 0;

  /**
   * Offsets whose fetch has failed, so the lookup path stops re-requesting
   * them.  lookup10FootByAbsOffset() runs once per render/poll tick, so without
   * this a permanently-404ing offset (typically one past the live edge, where
   * the segment is not yet on the CDN) is re-enqueued continuously for as long
   * as the user rests there.
   *
   * Only the lookup path consults this set — the live-edge timer and the
   * directional/initial batches still enqueue freely, so a transient failure is
   * retried by whichever of those covers the offset next.  A single unrecovered
   * slot is invisible in the UI because the MAX_ACCEPTABLE_GAP neighbour search
   * spans ±3 slots.  Cleared on resetThumbnails().
   */
  private m10FootFailedOffsets: Map<number, number> = new Map();

  /**
   * Last known playback position in the same scale as mStartTime/mEndTime
   * (absolute epoch seconds for live/startover, 0-based seconds for pure VOD).
   * Initialised to 0; updated by updateCurrentPosition() before initial fetch.
   */
  private m10FootCurrentPosition: number = 0;

  // ── Shared Debounce State (browser + 10-foot) ─────────────────────────────

  /**
   * Set true when PlayerState.LOADED fires.
   * Shared by both the browser and 10-foot debounce (the two paths are mutually
   * exclusive via m10FootIsActive).
   */
  private mIsPlayerLoaded: boolean = false;

  /** Set true when PlayerState.FIRST_FRAME fires. Shared by both paths. */
  private mIsFirstFrameReady: boolean = false;

  /**
   * DEBOUNCE_START_DELAY_MS timer after LOADED+FIRST_FRAME before the first
   * fetch (browser eager prefetch or 10-foot initial preload). Shared by both.
   */
  private mDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  /** When true, all 10-foot fetching is suspended (restriction API / blackout). */
  private m10FootIsFetchSuspended: boolean = false;

  /** Live-edge continuous fetch timer for 10-foot mode. */
  private m10FootLiveTimer: ReturnType<typeof setTimeout> | null = null;

  /** Next live thumbnail time offset to schedule in 10-foot mode. */
  private m10FootNextLiveTime: number | null = null;

  /**
   * The trick-play direction currently active (set by onSeekStarted, cleared
   * by onSeekConfirmed / setFetchSuspended / resetThumbnails).
   * While non-null, updateCurrentPosition() extends the look-ahead in this
   * direction instead of re-centering the bidirectional window.
   * null means normal playback mode (bidirectional window).
   */
  private m10FootActiveSeekDirection: Direction | null = null;

  /**
   * The furthest time offset (in the currently active seek direction) that
   * has already been committed to the pending queue or in-flight map.
   *
   * Set by onSeekStarted() to the last entry of the initial directional
   * batch.  extend10FootDirectionalBatch() walks DIRECTIONAL_PREFETCH_BATCH
   * steps BEYOND this frontier each time it fires, then advances the frontier
   * accordingly.
   *
   * This prevents the extend loop from re-walking the same range that
   * onSeekStarted() already queued (all those offsets are still in
   * m10FootPendingSet), which previously caused enqueued=0 on every call.
   *
   * null when no directional seek is active; reset on direction change,
   * onSeekConfirmed, setFetchSuspended(true), and resetThumbnails.
   */
  private m10FootDirectionalFrontier: number | null = null;

  /**
   * Active trick-play speed level, or null when no speed session is running
   * (idle, or a discrete short-press skip — which must keep the dense grid).
   *
   * Drives prefetch depth, concurrency and grid stride. Pushed in from the
   * ViewModel rather than pulled from TrickPlayManager: the two live in
   * separate subtrees and the existing data flow is one-way.
   */
  private m10FootSpeedLevel: SpeedLevel | null = null;

  /**
   * Timestamp (ms) of the last directional batch rebuild, and the pending
   * trailing-edge timer that coalesces rapid seeks. See
   * SEEK_BATCH_COALESCE_MS. The timer must be cleared in every teardown path
   * (onSeekConfirmed, setFetchSuspended, resetThumbnails) — it is a leak in
   * its own right, and it would otherwise rebuild a batch for a session that
   * has already ended.
   */
  private m10FootLastBatchAtMs: number = 0;
  private m10FootCoalesceTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Lower fetch bound in the 10-foot frame-epoch domain (converted seekable
   * range start), or null when no seekable bounds have been supplied yet.
   *
   * The frame-epoch domain is the one the cache keys and CDN filenames already
   * use: wall-clock epoch seconds of the thumbnail frame.  For LIVE the player
   * presentation clock is already epoch-aligned; for VOD/SOCU/CatchUp/NDVR the
   * player clock is 0-based from content start, so positions are shifted by
   * mStartTime (see to10FootDomain).
   *
   * These bounds track the *seekable* range — the live/available edge — not the
   * static airing window (mStartTime/mEndTime).  For LIVE the available edge
   * (seekRange.end) trails the airing end by many minutes; fetching past it
   * returns 404s.  For a still-airing SOCU program the available edge grows
   * toward the program end over time.
   */
  private m10FootFetchMinOffset: number | null = null;
  private m10FootFetchMaxOffset: number | null = null;

  // ── Constructor ──────────────────────────────────────────────────────────────

  /**
   * Creates a new ThumbnailManager with all state at default values.
   * Member defaults are initialized at declaration above per TypeScript convention;
   * the explicit constructor satisfies Module Class §9.2 of CodingGuidelines.
   */
  public constructor() {
    // Intentionally empty — member defaults are set at declaration.
  }

  // ── Private — Shared Debounce Core (browser + 10-foot) ────────────────────

  /**
   * Check whether both preconditions (LOADED + FIRST_FRAME) are met and params
   * are ready, then arm the DEBOUNCE_START_DELAY_MS timer before the first
   * fetch.  Shared by both platforms: the timer and the LOADED/FIRST_FRAME
   * monitoring are common; only the deferred action differs.
   *
   *   10-foot — start10FootInitialFetch() (sliding-window initial preload).
   *   browser — the eager build + prefetch sequence that loadThumbnails()
   *             used to run synchronously.
   *
   * The debounce exists to avoid competing with stream startup bandwidth.
   */
  private checkReadyToFetch(): void {
    if (
      !this.mIsPlayerLoaded ||
      !this.mIsFirstFrameReady ||
      this.mUrlTemplate === "" ||
      this.mDebounceTimer !== null
    ) {
      return;
    }
    // 10-foot-only preconditions: a resolved playback category and not suspended.
    if (
      this.m10FootIsActive &&
      (this.m10FootPlaybackCategory === null || this.m10FootIsFetchSuspended)
    ) {
      return;
    }
    const delayMs = this.m10FootConfig.DEBOUNCE_START_DELAY_MS;
    this.mDebounceTimer = setTimeout(() => {
      this.mDebounceTimer = null;
      if (this.m10FootIsActive) {
        this.start10FootInitialFetch();
      } else {
        this.mBrowser.start();
      }
    }, delayMs);
    Log.d(
      ThumbnailManager.TAG,
      "checkReadyToFetch(): debounce armed " + String(delayMs) + "ms"
    );
  }

  /**
   * Build the ordered offset list for the initial preload (§3.1).
   *
   *   LIVE — backward from the live edge over the available behind-live span
   *          [liveEdge − 10 min, liveEdge].  The forward (future) side does not
   *          exist yet and is covered by the live-edge timer.
   *   VOD  — forward from the current position over up to 10 min, clamped into
   *          the available range.
   *
   * Anchoring is robust to a late/stale playhead: on device
   * getPlayerCurrentTime() is 0 at load and the real position is pushed
   * asynchronously, so m10FootCurrentPosition may still be stale when the
   * debounce fires.  Anchoring LIVE at the seekable edge (when known) avoids an
   * empty preload (a backward walk that starts below the buffer start).
   */
  private build10FootInitialOffsets(cap: number, interval: number): number[] {
    const offsets: number[] = [];
    const minOffset = this.get10FootMinOffset();
    const maxOffset = this.get10FootMaxOffset();

    if (this.m10FootPlaybackCategory === "LIVE") {
      let startOffset =
        this.m10FootFetchMaxOffset !== null
          ? this.snap10FootOffset(maxOffset)
          : this.snap10FootOffset(this.m10FootCurrentPosition);
      if (startOffset > maxOffset) {
        startOffset = startOffset - interval;
      }
      let offset = startOffset;
      for (let i = 0; i < cap; i++) {
        if (offset < minOffset) {
          break;
        }
        offsets.push(offset);
        offset = offset - interval;
      }
      return offsets;
    }

    // VOD / catchup / startover.
    let startOffset = this.snap10FootOffset(this.m10FootCurrentPosition);
    if (startOffset < minOffset) {
      startOffset = minOffset;
    } else if (startOffset > maxOffset) {
      startOffset = maxOffset;
    }
    let offset = startOffset;
    for (let i = 0; i < cap; i++) {
      if (offset > maxOffset) {
        break;
      }
      if (offset >= minOffset) {
        offsets.push(offset);
      }
      offset = offset + interval;
    }
    return offsets;
  }

  private start10FootInitialFetch(): void {
    if (this.m10FootIsFetchSuspended || this.m10FootPlaybackCategory === null) {
      return;
    }

    const interval = this.mThumbnailsInterval;
    const cap = Math.ceil(
      this.m10FootConfig.INITIAL_PRELOAD_DURATION_SEC / interval
    );

    const offsets = this.build10FootInitialOffsets(cap, interval);

    for (let i = 0; i < offsets.length; i++) {
      this.enqueue10FootOffset(offsets[i], false);
    }

    Log.i(
      ThumbnailManager.TAG,
      "start10FootInitialFetch(): enqueued " +
        String(offsets.length) +
        " offsets"
    );

    this.drain10FootQueue();

    // The preload is now seeded. Nothing needs recording: steady state issues
    // no fetches of its own, so "seeded" and "not seeded" are indistinguishable
    // to every later branch.
    //
    // Note this deliberately does NOT disturb m10FootActiveSeekDirection.
    // onSeekStarted() is gated only on m10FootIsActive / category / suspended,
    // so a D-pad seek fired during the DEBOUNCE_START_DELAY_MS window has
    // already begun a directional session by the time this runs; clearing it
    // here would leave the look-ahead silently dead for the rest of the
    // session. Keying the look-ahead gate off the direction field alone makes
    // that failure mode structurally impossible.

    // Start the live-edge periodic timer for LIVE ONLY.  Gated strictly on the
    // playback category (derived from PlaybackContentType) — never on mIsLive —
    // so VOD-category playback (StartOver, CatchUp, NDVR, VOD) never appends
    // live-edge thumbnails, regardless of the isLive flag the thunk passes.
    if (this.m10FootPlaybackCategory === "LIVE") {
      this.schedule10FootLiveFetch();
    }
  }

  // ── Speed-adaptive tuning ─────────────────────────────────────────────────
  //
  // The policy itself lives in ThumbnailManager10FootConfig as pure functions
  // of (config, speedLevel, isTrickPlayActive, interval), where it is directly
  // unit-testable. These thin bindings supply the manager's current state so
  // call sites stay readable.

  /** @see resolveMaxParallelRequests */
  private resolve10FootMaxParallelRequests(): number {
    return resolveMaxParallelRequests(
      this.m10FootConfig,
      this.m10FootSpeedLevel,
      this.is10FootTrickPlayActive()
    );
  }

  /** @see resolveGridStride */
  private resolve10FootStride(): number {
    return resolveGridStride(this.m10FootConfig, this.m10FootSpeedLevel);
  }

  /** @see resolveDirectionalBatchSize */
  private resolve10FootBatchSize(): number {
    return resolveDirectionalBatchSize(
      this.m10FootConfig,
      this.m10FootSpeedLevel,
      this.mThumbnailsInterval
    );
  }

  /** @see resolveDirectionalBatchSpan */
  private resolve10FootBatchSpan(): number {
    return resolveDirectionalBatchSpan(
      this.m10FootConfig,
      this.m10FootSpeedLevel,
      this.mThumbnailsInterval
    );
  }

  /**
   * Set the active trick-play speed level (null when no speed session runs).
   *
   * Pushed from the ViewModel's trick-play poll and from onSeekStarted(). A
   * genuine level change re-bases the directional frontier onto the new grid,
   * because the stride — and therefore the batch span — has changed. The
   * unchanged-level early return keeps the 4 Hz push free.
   *
   * @param level - Active SpeedLevel, or null when not scrubbing at speed.
   */
  public setSeekVelocity(level: SpeedLevel | null): void {
    if (!this.m10FootIsActive) {
      return;
    }
    if (level === this.m10FootSpeedLevel) {
      return;
    }
    this.m10FootSpeedLevel = level;
    const direction = this.m10FootActiveSeekDirection;
    if (
      direction === null ||
      this.m10FootPlaybackCategory === null ||
      this.m10FootIsFetchSuspended
    ) {
      return;
    }
    // A level change invalidates the look-ahead: the stride moved, so the
    // frontier is on the wrong sub-lattice and the batch span it was measured
    // against no longer applies.
    this.reseed10FootLookAhead(direction, direction);
  }

  /**
   * Set the active trick-play seek direction (null when no scrub is running).
   *
   * The counterpart of setSeekVelocity(), and pushed from the same 4 Hz
   * trick-play poll. Direction used to be written ONLY by onSeekStarted(),
   * which made it impossible to correct once a caller got it wrong — and one
   * did: a counter-direction speed-cycle press reduces the level while
   * TrickPlayManager deliberately keeps seeking the original way (TP-FF-9), so
   * the button direction and the seek direction diverge. The manager then
   * cancelled the look-ahead the user was actually seeking into and extended
   * the opposite way for the rest of the session. Pushing the model's own
   * direction makes that self-correcting within one tick.
   *
   * @param direction - Active seek Direction, or null when not scrubbing.
   */
  public setSeekDirection(direction: Direction | null): void {
    if (!this.m10FootIsActive) {
      return;
    }
    if (direction === this.m10FootActiveSeekDirection) {
      return;
    }
    if (direction === null) {
      // The scrub ended by a route that did not call onSeekConfirmed (the
      // counter-direction press at L1 exits through TrickPlayManager.confirm()
      // internally). Tear the directional session down rather than leaving it
      // extending against a scrub that is over.
      this.clear10FootSeekSession();
      return;
    }
    const prevDirection = this.m10FootActiveSeekDirection;
    this.m10FootActiveSeekDirection = direction;
    if (this.m10FootPlaybackCategory === null || this.m10FootIsFetchSuspended) {
      return;
    }
    if (prevDirection !== null) {
      // A genuine reversal: drop the work queued the old way before seeding
      // the new one, or the two compete for the same concurrency slots.
      this.cancel10FootPendingInDirection(
        prevDirection,
        this.m10FootCurrentPosition
      );
    }
    this.reseed10FootLookAhead(direction, prevDirection);
  }

  /**
   * Re-anchor the directional look-ahead onto the current position and refill
   * it immediately.
   *
   * Shared by the two "the scrub changed shape" pushes — a new speed level and
   * a corrected direction. Both invalidate the frontier for the same reason:
   * it is expressed on a stride-dependent lattice measured in a specific
   * direction, so once either changes it no longer describes anything that was
   * queued.
   *
   * Re-anchoring alone is not enough — that would leave the frontier collapsed
   * onto the playhead with an empty queue behind it and nothing to refill it
   * until the next extension trigger, which is exactly how a speed change used
   * to freeze the preview on the previous frame. So this also queues the frame
   * the user is looking at right now and rebuilds the batch, never coalesced:
   * a change of shape is a change of intent, not a repeated gesture.
   *
   * The anchor uses snap10FootOffsetToStride, not the fine snap. An unaligned
   * base makes every later extension walk offsets phase-shifted from the ones
   * build10FootSeekBatch() queued, so the dedup stops matching for the rest of
   * the session.
   *
   * @param direction     - The direction the look-ahead now runs in.
   * @param prevDirection - Direction before this change, for the frontier rule.
   */
  private reseed10FootLookAhead(
    direction: Direction,
    prevDirection: Direction | null
  ): void {
    const anchor = this.snap10FootOffsetToStride(
      this.m10FootCurrentPosition,
      this.resolve10FootStride()
    );
    this.m10FootDirectionalFrontier = anchor;
    this.enqueue10FootOffset(anchor, true);
    this.drain10FootQueue();
    this.build10FootSeekBatch(
      direction,
      this.m10FootCurrentPosition,
      prevDirection
    );
  }

  /**
   * Dequeue pending offsets up to the available concurrency slots and
   * start fetching each one that is not already cached or in-flight.
   */
  private drain10FootQueue(): void {
    if (this.m10FootIsFetchSuspended) {
      return;
    }
    const slots =
      this.resolve10FootMaxParallelRequests() - this.m10FootActiveRequests;
    if (slots <= 0) {
      return;
    }
    // Free a batch of slots BEFORE issuing the next fetches rather than after
    // each one completes.  Two benefits over evicting in the fetch-success
    // handler: the cache never transiently exceeds MAX_CACHE_SIZE (that handler
    // set()s before evicting, so it peaks at cap+1), and the scan cost leaves
    // the completion path entirely.  The size guard keeps this O(1) once
    // headroom exists, which matters because drain is called on every
    // completion, every onSeekStarted and every lookup miss.
    if (
      this.is10FootTrickPlayActive() &&
      this.m10FootCache.size >= this.m10FootConfig.MAX_CACHE_SIZE
    ) {
      this.evictIfOverCap();
    }
    let fetched = 0;
    while (fetched < slots && this.m10FootPendingQueue.length > 0) {
      const offset = this.m10FootPendingQueue.shift();
      if (offset === undefined) {
        break;
      }
      // Keep the pending set in sync as items leave the queue.
      this.m10FootPendingSet.delete(offset);
      if (this.m10FootCache.has(offset) || this.m10FootInFlight.has(offset)) {
        continue;
      }
      this.fetch10FootEntry(offset);
      fetched++;
    }
  }

  /**
   * Fetch a single thumbnail for the given time offset in 10-foot mode.
   * On success: store in LRU cache, update lastAccessTimestamp, evict if over cap.
   * On abort: silent return.
   * On other error: log warn, continue queue.
   */
  private fetch10FootEntry(offset: number): void {
    const url = this.build10FootUrl(offset);
    if (url === null) {
      this.drain10FootQueue();
      return;
    }

    this.m10FootActiveRequests++;
    this.mSession.recordFetchIssued();
    this.mSession.recordActiveRequests(this.m10FootActiveRequests);
    const controller = new AbortController();
    this.m10FootInFlight.set(offset, controller);

    this.mSession.fetchGuarded(
      url,
      controller.signal,
      (blobUrl: string): void => {
        const entry: TThumbnailEntry = {
          time: offset,
          url,
          status: ThumbnailStatus.LOADED,
          imgBlob: blobUrl,
          abortController: null,
          lastAccessTimestamp: Date.now()
        };
        this.m10FootCache.set(offset, entry);
        this.mSession.recordCacheSize(this.m10FootCache.size);
        this.release10FootRequestSlot(offset);
        this.evictIfOverCap();
        this.drain10FootQueue();
        // Tell the View a new frame is available. A lookup that missed was
        // handed the last-known-good frame and this offset's fetch; without
        // this it would never learn the real one arrived.
        this.mSession.notifyThumbnailAvailable();
      },
      (reason: unknown): void => {
        this.on10FootFetchError(offset, reason);
      },
      (): void => {
        // An abort still held a concurrency slot; free it without recording a
        // failure, since the cancellation was ours.
        this.release10FootRequestSlot(offset);
      }
    );
  }

  /**
   * Handle a failed 10-foot thumbnail fetch: decrement active counter,
   * log the error, and continue draining the queue.
   *
   * Logged at WARN, not ERROR — see onFetchError() for the rationale behind
   * that deliberate deviation from CHECKLIST H-04 / CodingGuidelines §13.3.1.
   *
   * @param offset - The time offset (seconds) whose fetch failed.
   * @param reason - The caught error, or a short description when the failure
   *                 is a non-OK HTTP response rather than a thrown error.
   */
  private on10FootFetchError(offset: number, reason?: unknown): void {
    this.release10FootRequestSlot(offset);
    // Record the failure so the lookup path stops re-requesting this offset on
    // every render.  Prefetch paths are unaffected and still retry.
    //
    // Time-boxed rather than permanent: the commonest failure is a frame just
    // past the live edge whose segment is not on the CDN *yet*.  Blacklisting
    // it for the whole session meant the lookup path never asked again once the
    // CDN did publish it, leaving that position on the last-known-good frame
    // indefinitely.
    this.m10FootFailedOffsets.set(offset, Date.now() + FAILED_OFFSET_TTL_MS);
    if (this.m10FootFailedOffsets.size > MAX_FAILED_OFFSETS) {
      // Map preserves insertion order — drop the oldest record.
      const oldest = this.m10FootFailedOffsets.keys().next();
      if (oldest.done !== true) {
        this.m10FootFailedOffsets.delete(oldest.value);
      }
    }
    Log.w(
      ThumbnailManager.TAG,
      "on10FootFetchError(): failed for offset=" + String(offset),
      reason
    );
    this.drain10FootQueue();
  }

  /**
   * Single entry point for adding an offset to the pending-fetch queue.
   *
   * Centralises the three-way dedup (cached / in-flight / already pending)
   * that was previously duplicated verbatim at every enqueue site, and
   * enforces MAX_PENDING_QUEUE.
   *
   * Priority entries — the seek target and the live edge — are always admitted
   * and displace from the tail, because they are what the user is looking at
   * right now. Bulk look-ahead is simply refused once the queue is deep enough
   * that those offsets would be evicted before they could be displayed anyway.
   *
   * @param offset   - Absolute offset in the frame-epoch domain.
   * @param priority - True to unshift to the front of the queue.
   * @returns True if the offset was enqueued.
   */
  private enqueue10FootOffset(offset: number, priority: boolean): boolean {
    if (
      this.m10FootCache.has(offset) ||
      this.m10FootInFlight.has(offset) ||
      this.m10FootPendingSet.has(offset)
    ) {
      return false;
    }
    if (priority) {
      this.m10FootPendingQueue.unshift(offset);
      this.m10FootPendingSet.add(offset);
      this.trim10FootPendingQueue();
      return true;
    }
    if (
      this.m10FootPendingQueue.length >= this.m10FootConfig.MAX_PENDING_QUEUE
    ) {
      return false;
    }
    this.m10FootPendingQueue.push(offset);
    this.m10FootPendingSet.add(offset);
    return true;
  }

  /** Drop tail entries until the pending queue is within MAX_PENDING_QUEUE. */
  private trim10FootPendingQueue(): void {
    const max = this.m10FootConfig.MAX_PENDING_QUEUE;
    while (this.m10FootPendingQueue.length > max) {
      const dropped = this.m10FootPendingQueue.pop();
      if (dropped === undefined) {
        break;
      }
      this.m10FootPendingSet.delete(dropped);
    }
  }

  /**
   * True while an offset's failure record is still within its TTL.
   *
   * Self-pruning: an expired record is removed on read, so the offset gets one
   * fresh attempt rather than being suppressed forever.
   */
  private is10FootOffsetBlacklisted(offset: number): boolean {
    const expiresAt = this.m10FootFailedOffsets.get(offset);
    if (expiresAt === undefined) {
      return false;
    }
    if (expiresAt > Date.now()) {
      return true;
    }
    this.m10FootFailedOffsets.delete(offset);
    return false;
  }

  /**
   * Return the cached blob URL for a content-relative seek position, or trigger
   * an on-demand fetch and return the raw CDN URL as an immediate fallback.
   *
   * seekPositionSeconds is relative to content start.  Converted to the
   * absolute frame-epoch domain (cache-key domain) before lookup.
   */
  private get10FootThumbnail(seekPositionSeconds: number): string | null {
    return this.lookup10FootByAbsOffset(seekPositionSeconds + this.mStartTime);
  }

  /**
   * Search outward from `targetOffset` for a loaded frame, up to
   * MAX_ACCEPTABLE_GAP steps of one thumbnail interval either way.
   *
   * Probe order leans toward the direction of travel. Checking backward first
   * unconditionally makes a forward scrub display a frame lagging the position
   * label by up to gap * interval seconds; leading with the travel direction
   * keeps the preview just ahead of the target, which reads correctly while
   * fast-forwarding. Backward is also the idle default: with no active seek the
   * user is most likely resting at or just past a frame boundary, so the frame
   * at or before the target is the correct one to show.
   *
   * @param targetOffset - The exact (fine-grid) offset being displayed.
   * @returns The blob URL and how many steps away it was found, or null.
   */
  private probe10FootNeighbour(
    targetOffset: number
  ): { blobUrl: string; steps: number } | null {
    const gap = this.m10FootConfig.MAX_ACCEPTABLE_GAP;
    const interval = this.mThumbnailsInterval;
    const leadSign: number =
      this.m10FootActiveSeekDirection === Direction.Forward ? 1 : -1;
    for (let i = 1; i <= gap; i++) {
      const candidates = [
        targetOffset + leadSign * interval * i,
        targetOffset - leadSign * interval * i
      ];
      for (let c = 0; c < candidates.length; c++) {
        const entry = this.m10FootCache.get(candidates[c]);
        if (entry !== undefined && entry.imgBlob !== null) {
          entry.lastAccessTimestamp = Date.now();
          this.mSession.setLastDelivered(entry.imgBlob);
          return { blobUrl: entry.imgBlob, steps: i };
        }
      }
    }
    return null;
  }

  /**
   * How far off the exact frame a neighbour-probe hit is allowed to be, in
   * thumbnail-interval steps, before it counts as the wrong frame.
   *
   * Derived from the stride rather than fixed, because the stride is the only
   * reason a non-exact frame is ever acceptable: prefetching every Sth frame
   * puts the fine-grid target at most floor(S/2) steps from a cached one BY
   * CONSTRUCTION, and the probe exists to absorb exactly that. Anything
   * further away is not a designed approximation, it is stale coverage left
   * behind by a faster speed — see the call site.
   *
   * At stride 1 (L1, and every discrete skip) this is 0: nothing but the exact
   * frame will do.
   */
  private resolve10FootProbeTolerance(): number {
    return Math.floor(this.resolve10FootStride() / 2);
  }

  /**
   * Queue the exact frame for `targetOffset` at the front of the fetch queue,
   * unless it is already cached, in flight, pending or blacklisted.
   *
   * Shared by the two lookup paths that want the real frame: a total miss, and
   * a neighbour hit that is further away than the current stride justifies.
   *
   * @param targetOffset - Exact (fine-grid) offset the user is looking at.
   */
  private request10FootExactOffset(targetOffset: number): void {
    if (
      this.m10FootCache.has(targetOffset) ||
      this.m10FootInFlight.has(targetOffset) ||
      this.m10FootPendingSet.has(targetOffset) ||
      this.is10FootOffsetBlacklisted(targetOffset)
    ) {
      return;
    }
    // inWindow and the queue/concurrency figures identify WHY the frame was
    // not already there, which decides the right remedy:
    //   pendingQueue=0            → look-ahead never queued this offset; the
    //                               fix is prefetch depth (DIRECTIONAL_
    //                               PREFETCH_BATCH / LOOK_AHEAD_THRESHOLD).
    //   pendingQueue>0 and
    //   activeRequests at the cap → fetch throughput is the bottleneck; the
    //                               fix is concurrency.
    //   stride > 1                → the cache is coarser than the current
    //                               speed needs (a step-down); expected, and
    //                               this request is what re-densifies it.
    // Debug level (not info): this can fire on every lookup while the user
    // scrubs through an uncached region.
    const inWindow =
      targetOffset >= this.get10FootMinOffset() &&
      targetOffset <= this.get10FootMaxOffset();
    Log.d(
      ThumbnailManager.TAG,
      "request10FootExactOffset(): offset=" +
        String(targetOffset) +
        " inWindow=" +
        String(inWindow) +
        " bounds=[" +
        String(this.get10FootMinOffset()) +
        "," +
        String(this.get10FootMaxOffset()) +
        "] stride=" +
        String(this.resolve10FootStride()) +
        " cacheSize=" +
        String(this.m10FootCache.size) +
        " pendingQueue=" +
        String(this.m10FootPendingQueue.length) +
        " activeRequests=" +
        String(this.m10FootActiveRequests) +
        "/" +
        String(this.resolve10FootMaxParallelRequests())
    );
    this.enqueue10FootOffset(targetOffset, true);
    this.drain10FootQueue();
  }

  /**
   * Look up a cached thumbnail by its absolute frame-epoch offset (the domain
   * the cache keys and CDN filenames use).  Exact hit → nearest-neighbour
   * within MAX_ACCEPTABLE_GAP → on-demand fetch with a last-known-good
   * fallback.
   */
  private lookup10FootByAbsOffset(absPos: number): string | null {
    const targetOffset = this.snap10FootOffset(absPos);

    // 1. Exact cache hit.
    const exact = this.m10FootCache.get(targetOffset);
    if (exact !== undefined && exact.imgBlob !== null) {
      exact.lastAccessTimestamp = Date.now();
      this.mSession.recordExactDelivery(exact.imgBlob);
      return exact.imgBlob;
    }

    // 2. Nearest-neighbour within MAX_ACCEPTABLE_GAP.
    const neighbour = this.probe10FootNeighbour(targetOffset);
    if (neighbour !== null) {
      // A hit within the tolerance the CURRENT stride was designed for is the
      // right frame by construction — at stride S the fine-grid target sits at
      // most floor(S/2) steps from a strided frame, and fetching the exact one
      // would undo the whole point of striding at speed.
      //
      // Beyond that tolerance it is simply the wrong frame. That happens
      // whenever the cache is COARSER than the current speed warrants, which
      // is the normal state after a speed step-down: an L3 pass leaves a 60 s
      // lattice, and at L1 (stride 1, tolerance 0) every 12 s target then
      // resolves to a frame up to 30 s away — so the preview appeared to
      // advance only once per minute however slowly the user scrubbed. Show
      // the neighbour now (something is better than a blank box), but also
      // request the exact frame; the blob-available notification repaints as
      // soon as it lands.
      //
      // Self-limiting: the virtual target advances once per tick, so this adds
      // at most one request per frame actually displayed, and none at all
      // while the cache density already matches the speed.
      if (neighbour.steps > this.resolve10FootProbeTolerance()) {
        this.request10FootExactOffset(targetOffset);
      }
      return neighbour.blobUrl;
    }

    // 3. Nothing usable anywhere near the target — fetch it on demand.
    //
    // A genuine cache miss for a previously-unrequested offset. During healthy
    // operation these should be rare and always WITHIN bounds; an
    // out-of-bounds miss or a burst of misses while scrubbing indicates the
    // prefetch window is not tracking the user/edge correctly — which is what
    // request10FootExactOffset()'s diagnostic reports.
    this.request10FootExactOffset(targetOffset);

    // Never hand back the unfetched raw CDN URL here: that flashes
    // blank/broken in the <img> until the on-demand fetch above completes.
    // Keep showing the last genuinely-loaded frame instead; it upgrades to
    // the real one the moment the fetch lands. Only null if nothing has ever
    // loaded for this content.
    return this.mSession.getLastDelivered();
  }

  /**
   * True while a trick-play scrub is in progress.
   *
   * The single authority on that question. Everything that used to consult a
   * separate lifecycle enum now routes through here, so "the eviction window is
   * leaning", "we are batch-evicting" and "look-ahead extends on position
   * updates" cannot disagree — they read one field.
   */
  private is10FootTrickPlayActive(): boolean {
    return this.m10FootActiveSeekDirection !== null;
  }

  /**
   * End the directional seek session: no direction, no speed, no pending
   * coalesced rebuild, no frontier.
   *
   * Shared by onSeekConfirmed(), setFetchSuspended(true) and resetThumbnails().
   * These four fields are only ever meaningful together — a frontier without a
   * direction is unreachable, and a coalesce timer that survives the session it
   * belongs to rebuilds a batch for a scrub that already ended. Keeping the
   * clears in one place is what stops a future field being added to two of the
   * three paths.
   */
  private clear10FootSeekSession(): void {
    this.m10FootActiveSeekDirection = null;
    this.m10FootSpeedLevel = null;
    this.m10FootCoalesceTimer = clearTimer(this.m10FootCoalesceTimer);
    this.m10FootDirectionalFrontier = null;
  }

  /**
   * Release the concurrency slot held by an in-flight request.
   *
   * The clamp is not defensive tidiness: setFetchSuspended() and
   * resetThumbnails() zero m10FootActiveRequests while requests are still
   * outstanding, so a continuation landing afterwards would drive the counter
   * negative — and drain10FootQueue() would then compute more free slots than
   * the ceiling allows, permanently exceeding the concurrency limit.
   *
   * @param offset - The offset whose request has ended (success, error or abort).
   */
  private release10FootRequestSlot(offset: number): void {
    this.m10FootInFlight.delete(offset);
    this.m10FootActiveRequests--;
    if (this.m10FootActiveRequests < 0) {
      this.m10FootActiveRequests = 0;
    }
  }

  /** @see resolveEvictionBatchSize */
  private resolve10FootEvictionBatchSize(): number {
    return resolveEvictionBatchSize(
      this.m10FootConfig,
      this.is10FootTrickPlayActive()
    );
  }

  /** Revoke an entry's blob URL and remove it from the 10-foot cache. */
  private evict10FootEntry(entry: TThumbnailEntry): void {
    if (entry.imgBlob !== null) {
      URL.revokeObjectURL(entry.imgBlob);
      this.mSession.recordBlobRevoked();
    }
    this.m10FootCache.delete(entry.time);
    this.mSession.recordEviction();
  }

  /**
   * Snapshot the session state the eviction policy needs.
   *
   * @param category - Resolved playback category (never null at the call site).
   */
  private build10FootEvictionContext(
    category: PlaybackCategory
  ): TEvictionContext {
    return {
      config: this.m10FootConfig,
      category,
      currentPosition: this.m10FootCurrentPosition,
      direction: this.m10FootActiveSeekDirection,
      minOffset: this.get10FootMinOffset(),
      maxOffset: this.get10FootMaxOffset(),
      thumbnailIntervalSec: this.mThumbnailsInterval,
      pinnedBlobUrl: this.mSession.getLastDelivered()
    };
  }

  /**
   * Enforce MAX_CACHE_SIZE as a genuine hard ceiling.
   *
   * The decision — protected window, candidate tiers, how many to take from
   * each — is ThumbnailEvictionPolicy's; this owns only the effects (revoke,
   * delete, count, log).
   */
  private evictIfOverCap(): void {
    const cap = this.m10FootConfig.MAX_CACHE_SIZE;
    if (this.m10FootCache.size < cap) {
      return;
    }
    const category = this.m10FootPlaybackCategory;
    if (category === null) {
      return;
    }

    const context = this.build10FootEvictionContext(category);
    const window = computeProtectedWindow(context);
    const tiers = collectEvictionTiers(
      this.m10FootCache.values(),
      window,
      context
    );
    const victims = selectEvictionVictims(
      tiers,
      this.m10FootCache.size,
      cap,
      this.resolve10FootEvictionBatchSize()
    );

    for (let i = 0; i < victims.length; i++) {
      this.evict10FootEntry(victims[i]);
    }

    if (victims.length === 0) {
      return;
    }

    // One log per pass, not per entry — 25 Log.d calls per batch is its own
    // overhead on a TV console.
    Log.d(
      ThumbnailManager.TAG,
      "evictIfOverCap(): evicted " +
        String(victims.length) +
        " entries, cacheSize=" +
        String(this.m10FootCache.size)
    );
  }

  /**
   * Schedule periodic live-edge thumbnail fetches (LIVE category ONLY).
   * Stops automatically when fetching is suspended, 10-foot is disabled, or the
   * category is not LIVE (defensive — the timer is only ever started for LIVE).
   */
  private schedule10FootLiveFetch(): void {
    if (
      this.m10FootIsFetchSuspended ||
      !this.m10FootIsActive ||
      this.m10FootPlaybackCategory !== "LIVE"
    ) {
      return;
    }
    if (this.m10FootNextLiveTime === null) {
      // Initialise to the thumbnail immediately after the current position.
      // The initial LIVE fetch covers currentPos backward; the live timer
      // picks up the very next thumbnail that becomes available on the CDN.
      // Previously this was snap(currentPos) + INITIAL_PRELOAD_DURATION_SEC,
      // which pointed 10 minutes into the FUTURE — thumbnails that have not
      // aired yet — so the timer was silent for up to 10 minutes.
      this.m10FootNextLiveTime =
        this.snap10FootOffset(this.m10FootCurrentPosition) +
        this.mThumbnailsInterval;
    }
    const delayMs =
      this.m10FootConfig.LIVE_CONTINUOUS_FETCH_INTERVAL_SEC * 1000;
    this.m10FootLiveTimer = setTimeout(() => {
      this.m10FootLiveTimer = null;
      if (this.m10FootIsFetchSuspended || !this.m10FootIsActive) {
        return;
      }
      const nextTime = this.m10FootNextLiveTime;
      // For a live stream, mEndTime is the live-edge epoch captured at
      // content-load time and never updated.  Use the current wall-clock
      // time as the upper bound so new thumbnails continue to be fetched
      // as the live edge advances beyond the original mEndTime.
      // For VOD/catchup/startover, keep the static mEndTime check.
      let liveUpperBound: number;
      if (this.m10FootPlaybackCategory === "LIVE") {
        liveUpperBound = Math.floor(Date.now() / 1000);
      } else {
        liveUpperBound = this.mEndTime;
      }
      if (nextTime !== null && nextTime <= liveUpperBound) {
        this.enqueue10FootOffset(nextTime, true);
        this.m10FootNextLiveTime = nextTime + this.mThumbnailsInterval;
        this.drain10FootQueue();
      }
      this.schedule10FootLiveFetch();
    }, delayMs);
  }

  /**
   * Remove pending-queue entries that lie in `direction` relative to
   * `fromPosition`, and abort any matching in-flight requests too.
   * Ensures a direction reversal cleanly cancels both queued and running
   * fetches in the opposite direction.
   */
  private cancel10FootPendingInDirection(
    direction: Direction,
    fromPosition: number
  ): void {
    const isInDirection = (offset: number): boolean =>
      direction === Direction.Forward
        ? offset > fromPosition
        : offset < fromPosition;

    // 1. Remove from pending queue and keep the pending set in sync.
    const kept: number[] = [];
    for (let i = 0; i < this.m10FootPendingQueue.length; i++) {
      const offset = this.m10FootPendingQueue[i];
      if (isInDirection(offset)) {
        this.m10FootPendingSet.delete(offset);
      } else {
        kept.push(offset);
      }
    }
    this.m10FootPendingQueue = kept;

    // 2. Collect in-flight keys to abort (collect first, then abort to
    //    avoid mutating the Map during forEach iteration).
    const toAbort: number[] = [];
    this.m10FootInFlight.forEach((_controller, offset) => {
      if (isInDirection(offset)) {
        toAbort.push(offset);
      }
    });
    for (let i = 0; i < toAbort.length; i++) {
      const key = toAbort[i];
      const controller = this.m10FootInFlight.get(key);
      if (controller !== undefined) {
        controller.abort();
        this.release10FootRequestSlot(key);
      }
    }
  }

  /**
   * Build a batch of time offsets in the given direction for directional
   * pre-fetch during trick-play, capped at DIRECTIONAL_PREFETCH_BATCH.
   *
   * @param direction    - Direction.Forward or Direction.Backward.
   * @param fromPosition - Absolute start position (seconds) for the batch.
   * @returns Array of time offsets in the given direction.
   */
  private build10FootDirectionalBatch(
    direction: Direction,
    fromPosition: number
  ): number[] {
    const stride = this.resolve10FootStride();
    return this.walk10FootOffsets(
      this.snap10FootOffsetToStride(fromPosition, stride),
      direction,
      this.mThumbnailsInterval * stride,
      this.resolve10FootBatchSize()
    );
  }

  /**
   * Walk up to `cap` strided offsets outward from `base` in `direction`,
   * stopping at the seekable/available edge.
   *
   * The one place the look-ahead lattice is defined. Both the initial seek
   * batch and every subsequent frontier extension walk it — they differ only in
   * where they start, so keeping the walk itself in one function is what
   * guarantees the frontier arithmetic stays in step with what was queued.
   *
   * `base` itself is never emitted: the caller has either already enqueued it
   * (the seek target) or already covered it (the frontier).
   *
   * @param base      - Anchor offset, already snapped to the coarse grid.
   * @param direction - Direction.Forward (+1) or Direction.Backward (-1).
   * @param step      - Seconds between consecutive offsets (interval × stride).
   * @param cap       - Maximum number of offsets to emit.
   */
  private walk10FootOffsets(
    base: number,
    direction: Direction,
    step: number,
    cap: number
  ): number[] {
    const offsets: number[] = [];
    const minOffset = this.get10FootMinOffset();
    const maxOffset = this.get10FootMaxOffset();
    for (let i = 1; i <= cap; i++) {
      const offset = base + direction * i * step;
      // Bound by the seekable/available edge — for LIVE the forward edge is the
      // live edge (seekRange.end), well short of mEndTime (the airing end), so
      // this is what prevents queuing not-yet-broadcast offsets that 404.
      if (offset < minOffset || offset > maxOffset) {
        break;
      }
      offsets.push(offset);
    }
    return offsets;
  }

  /**
   * Extend the directional look-ahead beyond the current frontier.
   * Called by updateCurrentPosition() while m10FootActiveSeekDirection is set.
   *
   * Walks DIRECTIONAL_PREFETCH_BATCH steps from m10FootDirectionalFrontier
   * (not from the current position) and enqueues every uncached, non-in-flight,
   * non-pending offset.  After each call the frontier advances by one full
   * batch width so the next call covers the next genuinely new range.
   *
   * Walking from the frontier (rather than from snap(currentPosition)) is
   * critical: onSeekStarted() already queued all slots in the
   * [snap(fromPos) … snap(fromPos)+BATCH*interval] range, so they are all in
   * m10FootPendingSet.  Walking from the same base would find enqueued=0 every
   * time and never pre-fetch the subsequent batch, leaving the user at the
   * frontier with no look-ahead.
   *
   * LIVE guard: forward offsets past m10FootCurrentPosition are skipped (those
   * thumbnails have not been broadcast yet and would 404).
   */
  private extend10FootDirectionalBatch(): void {
    if (
      this.m10FootActiveSeekDirection === null ||
      this.m10FootPlaybackCategory === null ||
      this.m10FootDirectionalFrontier === null
    ) {
      return;
    }
    const direction = this.m10FootActiveSeekDirection;
    // Stride and batch size must match build10FootDirectionalBatch() exactly,
    // or the frontier drifts out of step with what was actually queued — which
    // is why both walk via walk10FootOffsets().
    const stride = this.resolve10FootStride();
    const step = this.mThumbnailsInterval * stride;
    const cap = this.resolve10FootBatchSize();
    // Start from the frontier — the end of the last committed batch.
    const frontier = this.m10FootDirectionalFrontier;

    const batch = this.walk10FootOffsets(frontier, direction, step, cap);
    let enqueued = 0;
    for (let i = 0; i < batch.length; i++) {
      if (this.enqueue10FootOffset(batch[i], false)) {
        enqueued++;
      }
    }
    // Always advance the frontier by one full batch width, even if some of
    // this range was already cached or in-flight.  This ensures the NEXT
    // call starts from the true next uncovered region.
    this.m10FootDirectionalFrontier = frontier + direction * cap * step;

    if (enqueued > 0) {
      this.drain10FootQueue();
    }
  }

  // ── Private — 10-Foot Helpers ─────────────────────────────────────────────

  /**
   * Convert a raw player position into the 10-foot frame-epoch domain (the
   * domain the cache keys, snap grid and CDN filenames use).
   *
   * Scale is detected by MAGNITUDE rather than the (unreliable) live flag,
   * because the player clock's scale varies by category and was the source of
   * repeated cache-miss regressions:
   *   - Wall-clock epoch positions (LIVE presentation time, and any category
   *     whose player clock is epoch-aligned) are ~1.7e9 — already in the
   *     cache-key domain — so they pass through unchanged.
   *   - Content-relative 0-based positions (VOD/SOCU/CatchUp/NDVR, 0…duration)
   *     are shifted by mStartTime to land on the frame's airing epoch.
   *
   * EPOCH_SCALE_THRESHOLD_SEC (1e8 ≈ year 1973 in epoch seconds) separates the
   * two with a huge margin: no content duration approaches it and every real
   * wall-clock epoch far exceeds it.  This keeps the prefetch and lookup paths
   * (both of which call this) identical regardless of the per-category quirks
   * in how the View layer reports positions, so cache hits always line up.
   */
  private to10FootDomain(playerPosition: number): number {
    return toFrameEpochDomain(playerPosition, this.mStartTime);
  }

  /**
   * Lower fetch bound (frame-epoch domain): the seekable-range start when
   * supplied, otherwise the content start.  Never below mStartTime.
   */
  private get10FootMinOffset(): number {
    if (this.m10FootFetchMinOffset === null) {
      return this.mStartTime;
    }
    return Math.max(this.m10FootFetchMinOffset, this.mStartTime);
  }

  /**
   * Upper fetch bound (frame-epoch domain): the seekable/available edge when
   * supplied (live edge for LIVE, grown edge for still-airing SOCU), otherwise
   * the content end.  Never above mEndTime.  This is the boundary that keeps
   * forward prefetch from requesting not-yet-available thumbnails (404s).
   */
  private get10FootMaxOffset(): number {
    if (this.m10FootFetchMaxOffset === null) {
      return this.mEndTime;
    }
    return Math.min(this.m10FootFetchMaxOffset, this.mEndTime);
  }

  /**
   * Snap an absolute content time (epoch seconds for live, 0-based seconds for
   * VOD) to the nearest thumbnail-interval boundary anchored at mStartTime.
   * Unclamped — callers apply their own upper bound, which differs by path
   * (mEndTime for 10-foot, getMaxThumbnailTime() for browser).
   *
   * Anchoring to mStartTime (content-relative) is critical: thumbnails are
   * generated at mStartTime + k * THUMBNAIL_INTERVAL, so snapping to GLOBAL
   * multiples of the interval yields keys that exist in neither map whenever
   * mStartTime % THUMBNAIL_INTERVAL !== 0 — every exact lookup and every
   * neighbour probe misses.  Both platforms share this helper so neither can
   * drift back onto the global grid.
   *
   * GUARANTEE: the result is always >= mStartTime, because snappedRelative is
   * floored at 0.  Callers rely on this and therefore apply an upper bound
   * only; do not remove the floor below without restoring their lower clamps.
   */
  private snapToThumbnailGrid(absolutePositionSeconds: number): number {
    return this.snapToStep(absolutePositionSeconds, this.mThumbnailsInterval);
  }

  /**
   * Round an absolute position to the nearest multiple of `stepSeconds`
   * measured from mStartTime, floored at mStartTime.
   *
   * The single rounding primitive behind both grids — the fine 12-second
   * lookup grid (stepSeconds = interval) and the coarse prefetch sub-lattice
   * (stepSeconds = interval × stride). They were separate implementations of
   * the same arithmetic differing only in the modulus, which is precisely the
   * kind of duplication that lets one grid drift off the other.
   *
   * @param absolutePositionSeconds - Position in the frame-epoch domain.
   * @param stepSeconds             - Grid spacing in seconds (> 0).
   */
  private snapToStep(
    absolutePositionSeconds: number,
    stepSeconds: number
  ): number {
    return snapToStep(absolutePositionSeconds, this.mStartTime, stepSeconds);
  }

  /**
   * Snap to the thumbnail grid and clamp to [mStartTime, mEndTime] — the
   * 10-foot cache-key domain.
   *
   * All callers pass absolute time.  get10FootThumbnail() is the only caller
   * that receives relative input and must add mStartTime before calling here.
   */
  private snap10FootOffset(absolutePositionSeconds: number): number {
    // Upper bound only — snapToThumbnailGrid() already guarantees >= mStartTime.
    const snapped = this.snapToThumbnailGrid(absolutePositionSeconds);
    return snapped > this.mEndTime ? this.mEndTime : snapped;
  }

  /**
   * Snap to the COARSE prefetch grid — every `stride` thumbnail intervals,
   * anchored at mStartTime — then clamp to [mStartTime, mEndTime].
   *
   * Stride 1 is identical to snap10FootOffset(). This is deliberately separate
   * from that function because only the PREFETCH path may use the coarse grid:
   * the lookup path must keep snapping to the fine 12-second grid so its
   * nearest-neighbour probe can find the strided frames sitting around the
   * display target. Striding both would defeat the probe and miss everything.
   *
   * Every strided offset is still a real frame — the CDN places one at each
   * mStartTime + k*12 and a stride simply selects a sub-lattice of those.
   *
   * @param absolutePositionSeconds - Absolute time in the frame-epoch domain.
   * @param stride                  - Grid stride in thumbnail intervals (>= 1).
   */
  private snap10FootOffsetToStride(
    absolutePositionSeconds: number,
    stride: number
  ): number {
    // Stride 1 makes the step the plain interval, so this needs no special
    // case — it reduces to snap10FootOffset() by arithmetic.
    const snapped = this.snapToStep(
      absolutePositionSeconds,
      this.mThumbnailsInterval * stride
    );
    // Upper bound only — snapToStep() never returns below mStartTime.
    return snapped > this.mEndTime ? this.mEndTime : snapped;
  }

  /**
   * Build the CDN URL for a given time offset using the existing filename
   * convention (floor(offset/MEDIA_SEGMENT_DURATION), made odd).
   * Returns null if the URL template is empty.
   */
  private build10FootUrl(offset: number): string | null {
    return buildThumbnailUrl(this.mUrlTemplate, offset);
  }

  // ── Public API ───────────────────────────────────────────────────────────

  /**
   * The thumbnail URL template currently loaded, with its `{FILENAME}`
   * placeholder unsubstituted, or `""` when no template is in use.
   *
   * Diagnostics only — the Video Debug Overlay shows it so QA can see
   * which thumbnail host and path an airing resolved to, which is the
   * usual cause of a missing filmstrip. Nothing in the thumbnail pipeline
   * reads it back.
   *
   * @returns The active template, or `""` when thumbnails are unavailable.
   */
  public getUrlTemplate(): string {
    return this.mUrlTemplate;
  }

  // ── 10-Foot Public Methods ────────────────────────────────────────────────

  /**
   * Activate the 10-foot sliding-window strategy.
   * Must be called before loadThumbnails() to take effect.
   * No-op on subsequent calls (m10FootIsActive remains true; config is not re-merged).
   *
   * @param config - Optional partial overrides merged with DEFAULT_10FOOT_CONFIG.
   */
  public enable10FootStrategy(config?: Partial<T10FootThumbnailConfig>): void {
    if (this.m10FootIsActive) {
      return;
    }
    if (config !== undefined) {
      this.m10FootConfig = { ...DEFAULT_10FOOT_CONFIG, ...config };
    } else {
      this.m10FootConfig = { ...DEFAULT_10FOOT_CONFIG };
    }
    // The queue must be able to hold a full initial preload plus one
    // directional batch, or start10FootInitialFetch() would be silently
    // truncated by the cap it shares with bulk look-ahead.
    const minQueue =
      Math.ceil(
        this.m10FootConfig.INITIAL_PRELOAD_DURATION_SEC / THUMBNAIL_INTERVAL
      ) + this.m10FootConfig.DIRECTIONAL_PREFETCH_BATCH;
    if (this.m10FootConfig.MAX_PENDING_QUEUE < minQueue) {
      this.m10FootConfig.MAX_PENDING_QUEUE = minQueue;
    }
    this.m10FootIsActive = true;
    Log.d(ThumbnailManager.TAG, "enable10FootStrategy(): activated");
  }

  /**
   * Notify that PlayerState.LOADED has fired.
   * Triggers the debounce check for the initial pre-fetch on both platforms
   * (10-foot initial preload or browser eager prefetch).
   */
  public notifyPlayerLoaded(): void {
    this.mIsPlayerLoaded = true;
    this.checkReadyToFetch();
  }

  /**
   * Notify that PlayerState.FIRST_FRAME has fired.
   * Triggers the debounce check for the initial pre-fetch on both platforms
   * (10-foot initial preload or browser eager prefetch).
   */
  public notifyFirstFrame(): void {
    this.mIsFirstFrameReady = true;
    this.checkReadyToFetch();
  }

  /**
   * Supply the current seekable range (raw player scale, e.g. the values from
   * VideoPlaybackController.getSeekRange()).  Converted into the frame-epoch
   * domain and used as the forward/backward fetch caps so prefetch never
   * requests thumbnails past the live/available edge or before the buffer
   * start.  Should be called whenever the seek range changes (it advances as
   * the live edge moves).  No-op if 10-foot strategy is inactive.
   *
   * @param rangeStart - Seekable range start in raw player seconds.
   * @param rangeEnd   - Seekable range end in raw player seconds.
   */
  public setSeekableBounds(rangeStart: number, rangeEnd: number): void {
    if (!this.m10FootIsActive || this.m10FootPlaybackCategory === null) {
      return;
    }
    if (rangeEnd <= rangeStart) {
      return;
    }
    this.m10FootFetchMinOffset = this.to10FootDomain(rangeStart);
    this.m10FootFetchMaxOffset = this.to10FootDomain(rangeEnd);
  }

  /**
   * Update the current playback position.
   *
   * Per PRD §3.3, after the initial preload thumbnails are fetched ONLY while a
   * seek is active: when a directional seek is in progress this extends the
   * look-ahead in the seek direction (gated by the 70% threshold).  During
   * normal playback it merely tracks the position (used for eviction relevance
   * and as the anchor for the next seek) and triggers NO fetches — LIVE's
   * growing edge is handled separately by the live-edge timer.
   *
   * No-op if 10-foot strategy is not active.
   *
   * @param position - Current playback time in raw player seconds (epoch for
   *                   LIVE, 0-based for VOD/SOCU); normalised internally.
   */
  public updateCurrentPosition(position: number): void {
    if (!this.m10FootIsActive || this.m10FootPlaybackCategory === null) {
      return;
    }
    // Normalise the raw player position into the frame-epoch domain so it is
    // directly comparable with mStartTime/mEndTime and the cache keys.
    position = this.to10FootDomain(position);
    const delta = Math.abs(position - this.m10FootCurrentPosition);
    this.m10FootCurrentPosition = position;
    // Fetch only during an active seek (never while suspended).
    //
    // Gated on movement, NOT on a full thumbnail interval of movement. The
    // virtual seek target advances once per TICK_INTERVAL_MS by SPEED_VALUE
    // seconds while this is called at the 250 ms poll cadence, so an
    // interval-sized gate is unreachable at L1 (3 s/tick against a 12 s
    // interval) — look-ahead never extended for the whole hold, and every
    // displayed frame fell through to the single on-demand miss fetch.
    // maybeExtend10FootDirectionalBatch() is O(1) and already throttled by
    // LOOK_AHEAD_THRESHOLD, which is the speed-aware place for that decision.
    if (
      this.is10FootTrickPlayActive() &&
      !this.m10FootIsFetchSuspended &&
      delta > 0
    ) {
      this.maybeExtend10FootDirectionalBatch();
    }
  }

  /**
   * Schedule the next directional look-ahead batch only once the virtual
   * position has consumed LOOK_AHEAD_THRESHOLD (default 70%) of the current
   * batch's active-side span (PRD §3.3.3).
   *
   * This replaces the previous "extend on every position tick" behaviour, which
   * advanced the frontier a full batch each tick regardless of how far the user
   * moved — letting the look-ahead race past the seekable edge (then stall with
   * nothing left to enqueue) or detach from a slowly-moving user.  Gating on
   * actual consumption keeps the frontier ~one batch ahead of the user.
   */
  private maybeExtend10FootDirectionalBatch(): void {
    const direction = this.m10FootActiveSeekDirection;
    if (direction === null || this.m10FootDirectionalFrontier === null) {
      return;
    }
    // Must match the builder and the frontier advance — see
    // resolve10FootBatchSpan().
    const batchSpan = this.resolve10FootBatchSpan();
    // Signed look-ahead distance still ahead of the user in the seek direction.
    const remaining =
      direction *
      (this.m10FootDirectionalFrontier - this.m10FootCurrentPosition);
    const extendWhenRemainingBelow =
      (1 - this.m10FootConfig.LOOK_AHEAD_THRESHOLD) * batchSpan;
    if (remaining <= extendWhenRemainingBelow) {
      this.extend10FootDirectionalBatch();
    }
  }

  /**
   * Notify that a seek has started in a given direction from a given position.
   * Records the active seek direction (enables look-ahead extension on every
   * subsequent position update), cancels opposite-direction pending/in-flight
   * entries, then immediately enqueues:
   *   1. The snap of `fromPosition` itself at highest priority (unshift) so the
   *      thumbnail for the exact seek target is fetched before the look-ahead
   *      entries — eliminating the nearest-neighbour fallback race where the
   *      SeekBar renders before the target blob has arrived.
   *   2. The next DIRECTIONAL_PREFETCH_BATCH thumbnails beyond the target,
   *      appended in direction order for smooth scrolling look-ahead.
   *
   * Also seeds (or advances) m10FootDirectionalFrontier to the end of this
   * batch.  extend10FootDirectionalBatch() starts from that frontier so that
   * each subsequent position update queues a genuinely NEW batch of thumbnails
   * beyond what this call already committed — not a re-walk of the same range.
   *
   * No-op if 10-foot strategy is not active.
   *
   * @param direction    - Direction.Forward or Direction.Backward.
   * @param fromPosition - Seek target position in seconds (absolute, post-skip).
   * @param level        - Active SpeedLevel for a hold / speed-cycle session,
   *                       or null for a discrete skip (which keeps the dense
   *                       grid). Omit to leave the current velocity unchanged.
   */
  public onSeekStarted(
    direction: Direction,
    fromPosition: number,
    level?: SpeedLevel | null
  ): void {
    if (!this.m10FootIsActive || this.m10FootPlaybackCategory === null) {
      return;
    }
    if (this.m10FootIsFetchSuspended) {
      return;
    }
    if (level !== undefined) {
      this.setSeekVelocity(level);
    }
    // Normalise the raw seek target into the frame-epoch domain (see
    // to10FootDomain) so snapping, bounds and cache keys all line up.
    fromPosition = this.to10FootDomain(fromPosition);
    // Capture the previous direction before overwriting — needed to decide
    // whether to reset or advance the directional frontier below.
    const prevDirection = this.m10FootActiveSeekDirection;
    // Record active direction so updateCurrentPosition() extends look-ahead
    // in this direction rather than re-centering the window.
    this.m10FootActiveSeekDirection = direction;
    this.cancel10FootPendingInDirection(
      direction === Direction.Forward ? Direction.Backward : Direction.Forward,
      fromPosition
    );
    // Priority 1 — snap of fromPosition itself (unshift = front of queue).
    // This thumbnail is what the SeekBar displays immediately after the seek
    // fires.  Without this, the first render sees a cache miss and falls back
    // to the nearest cached neighbour (stale), then updates when the blob
    // arrives.  Fetching it first closes that gap.
    // Snapped to the COARSE grid so this priority entry lands on the same
    // lattice the look-ahead walks. On the fine grid it would be an orphan the
    // batch never revisits, and the lookup's neighbour probe would have to
    // cover for it on every frame.
    const stride = this.resolve10FootStride();
    const snapTarget = this.snap10FootOffsetToStride(fromPosition, stride);
    this.enqueue10FootOffset(snapTarget, true);
    this.drain10FootQueue();

    // Priority 2 — the directional look-ahead batch. Coalesced, because unlike
    // the target above it is not what the user is looking at right now.
    // Hammering the skip key produces a genuine seek per press (each of which
    // must move the target), but rebuilding a whole batch per press piles up
    // offsets that are fetched and evicted before they can ever be displayed.
    //
    // A direction REVERSAL is never coalesced: it is a change of intent rather
    // than a repeat of the same gesture, and cancel10FootPendingInDirection()
    // above has just dropped the opposite-direction entries — deferring the
    // rebuild would leave look-ahead pointing the wrong way with an empty
    // queue behind it.
    const now = Date.now();
    const coalesceMs = this.m10FootConfig.SEEK_BATCH_COALESCE_MS;
    const isReversal = prevDirection !== null && prevDirection !== direction;
    if (!isReversal && now - this.m10FootLastBatchAtMs < coalesceMs) {
      this.m10FootCoalesceTimer = clearTimer(this.m10FootCoalesceTimer);
      const remaining = coalesceMs - (now - this.m10FootLastBatchAtMs);
      this.m10FootCoalesceTimer = setTimeout((): void => {
        this.m10FootCoalesceTimer = null;
        const dir = this.m10FootActiveSeekDirection;
        if (dir === null || this.m10FootIsFetchSuspended) {
          return;
        }
        // Rebuild from the CURRENT position, not the captured one — by now the
        // user has skipped several more times and the original is stale.
        this.build10FootSeekBatch(dir, this.m10FootCurrentPosition, dir);
      }, remaining);
      return;
    }
    this.m10FootLastBatchAtMs = now;
    this.build10FootSeekBatch(direction, fromPosition, prevDirection);
  }

  /**
   * Queue the directional look-ahead batch for a seek and advance the frontier.
   *
   * Split out of onSeekStarted() so it can run either inline or on the trailing
   * edge of the coalesce window. Everything here is look-ahead — the frame the
   * user is actually looking at is enqueued synchronously by the caller and is
   * never delayed.
   *
   * @param direction     - Active seek direction.
   * @param fromPosition  - Batch anchor, already in the frame-epoch domain.
   * @param prevDirection - Direction before this seek, for the frontier rule.
   */
  private build10FootSeekBatch(
    direction: Direction,
    fromPosition: number,
    prevDirection: Direction | null
  ): void {
    const stride = this.resolve10FootStride();
    const snapTarget = this.snap10FootOffsetToStride(fromPosition, stride);
    const batch = this.build10FootDirectionalBatch(direction, fromPosition);
    for (let i = 0; i < batch.length; i++) {
      this.enqueue10FootOffset(batch[i], false);
    }
    // Seed or advance the directional frontier.
    //
    // The frontier marks the END of the range already committed to the queue.
    // build10FootDirectionalBatch walks resolve10FootBatchSize() steps of
    // (interval * stride) from the coarse snap of fromPosition, so the last
    // entry is snapTarget + direction * batchSpan. Using resolve10FootBatchSpan()
    // here keeps this in lockstep with the builder and the extension threshold;
    // if the three ever disagree the frontier drifts and look-ahead either
    // stalls or re-walks offsets already in the pending set.
    //
    // extend10FootDirectionalBatch() will start from this frontier on the next
    // position update, ensuring it queues genuinely new entries — not a re-walk
    // of the range the batch loop above already covered.
    const batchFrontier =
      snapTarget + direction * this.resolve10FootBatchSpan();
    if (
      prevDirection !== direction ||
      this.m10FootDirectionalFrontier === null
    ) {
      // Direction changed or first seek: set frontier to the end of this batch.
      this.m10FootDirectionalFrontier = batchFrontier;
    } else if (direction === Direction.Forward) {
      // Same direction forward: advance frontier only, never regress.
      this.m10FootDirectionalFrontier = Math.max(
        this.m10FootDirectionalFrontier,
        batchFrontier
      );
    } else {
      // Same direction backward: advance frontier only (lower = further back).
      this.m10FootDirectionalFrontier = Math.min(
        this.m10FootDirectionalFrontier,
        batchFrontier
      );
    }
    this.drain10FootQueue();
  }

  /**
   * Notify that a seek session has been confirmed or cancelled.
   *
   * Ends the directional session — clears direction, speed, the coalesce timer
   * and the frontier — and re-anchors the current position, which is what the
   * eviction-protected window is centred on.  Issues no fetches of its own.
   *
   * No-op if 10-foot strategy is not active.
   *
   * @param position - Confirmed playback position in seconds.
   */
  public onSeekConfirmed(position: number): void {
    if (!this.m10FootIsActive) {
      return;
    }
    // Clear trick-play mode: subsequent position updates use the normal
    // bidirectional window instead of continuing the directional look-ahead,
    // and a future onSeekStarted starts from a fresh frontier.
    this.clear10FootSeekSession();
    this.updateCurrentPosition(position);
  }

  /**
   * Suspend or resume thumbnail fetching for whichever strategy is active.
   *
   * 10-foot, when suspended (trick-play restriction active, blackout, or an
   * error OSD/Dialog covering the player):
   *   - All in-flight requests are aborted.
   *   - Pending queue is cleared.
   *   - Live-edge timer is stopped.
   *
   *   When resumed, if params are already loaded, the initial fetch is
   *   restarted.
   *
   * Browser: delegates to BrowserThumbnailStrategy.setFetchSuspended(),
   * which stops new fetches from starting without discarding the cache or
   * queue (see that method for details).
   *
   * @param suspended - True to suspend, false to resume.
   */
  public setFetchSuspended(suspended: boolean): void {
    if (!this.m10FootIsActive) {
      this.mBrowser.setFetchSuspended(suspended);
      return;
    }
    if (suspended === this.m10FootIsFetchSuspended) {
      return;
    }
    this.m10FootIsFetchSuspended = suspended;
    if (suspended) {
      // Invalidate in-flight continuations before aborting — this path zeroes
      // m10FootActiveRequests below, so a late success arriving afterwards
      // would otherwise decrement it negative and break the concurrency
      // ceiling for the rest of the session. See ThumbnailSession.
      this.mSession.invalidate();
      // Abort all in-flight 10-foot fetches.
      this.m10FootInFlight.forEach((controller) => {
        controller.abort();
      });
      this.m10FootInFlight = new Map();
      this.m10FootActiveRequests = 0;
      this.m10FootPendingQueue = [];
      this.m10FootPendingSet = new Set();
      // Clear the active trick-play direction and frontier so the resumed state
      // starts fresh in normal-playback mode. Clearing the direction is also
      // what stops directional look-ahead firing on the first position update
      // after resume, which would extend the frontier before the re-seeded
      // initial preload has even started draining.
      this.clear10FootSeekSession();
      this.mDebounceTimer = clearTimer(this.mDebounceTimer);
      this.m10FootLiveTimer = clearTimer(this.m10FootLiveTimer);
      Log.i(ThumbnailManager.TAG, "setFetchSuspended(): suspended");
    } else {
      Log.i(ThumbnailManager.TAG, "setFetchSuspended(): resumed");
      if (
        this.mUrlTemplate !== "" &&
        this.m10FootPlaybackCategory !== null &&
        this.mIsPlayerLoaded &&
        this.mIsFirstFrameReady
      ) {
        this.start10FootInitialFetch();
      }
    }
  }

  // ── Original Public API ──────────────────────────────────────────────────

  /**
   * Initialise the thumbnail pipeline for a piece of content.
   * Call on the PLAYING event (not on playback request).
   *
   * On 10-foot devices, pass `playbackContentType` to activate the
   * sliding-window strategy instead of the eager 600-entry pre-fetch.
   *
   * Parameters are based on current mobi conventions.
   * May change once URL construction is finalised or if we decide
   * to accept a playbackItem object instead of primitives.
   *
   * @param startTime           Content start epoch (seconds) or 0 for VOD.
   * @param endTime             Content end epoch (seconds) or duration for VOD.
   * @param urlTemplate         URL template with `{FILENAME}` placeholder.
   * @param isLive              Whether the content is a live stream.
   * @param playbackContentType Optional — passed by TV thunk to activate 10-foot path.
   */
  public loadThumbnails(
    startTime: number,
    endTime: number,
    urlTemplate: string,
    isLive: boolean,
    playbackContentType?: PlaybackContentType
  ): void {
    if (!urlTemplate) {
      Log.w(
        ThumbnailManager.TAG,
        "loadThumbnails(): empty URL template — aborting"
      );
      return;
    }

    // ── 10-foot path ──────────────────────────────────────────────────────
    if (this.m10FootIsActive && playbackContentType !== undefined) {
      const category = mapToPlaybackCategory(playbackContentType);
      if (category !== null) {
        this.m10FootPlaybackCategory = category;
        this.mStartTime = startTime;
        this.mEndTime = endTime;
        this.mUrlTemplate = urlTemplate;
        this.mThumbnailsInterval = THUMBNAIL_INTERVAL;
        // Seed current position to the content start so the first
        // updateCurrentPosition() call does not produce a massive delta
        // (epoch vs 0) against a zero-initialised position.
        this.m10FootCurrentPosition = startTime;
        Log.i(
          ThumbnailManager.TAG,
          "loadThumbnails(): 10-foot mode category=" +
            category +
            " start=" +
            String(startTime) +
            " end=" +
            String(endTime)
        );
        this.checkReadyToFetch();
        return;
      }
    }

    // ── Browser / fallback path ──────────────────────────────────────────
    // Params are handed to the strategy, but the eager build + prefetch is
    // deferred to checkReadyToFetch() so it starts only after LOADED +
    // FIRST_FRAME plus the debounce delay (avoids competing with stream
    // startup) — the same trigger the 10-foot path uses.
    this.mBrowser.load(startTime, endTime, urlTemplate, isLive);
    // Retained here purely as the readiness latch checkReadyToFetch() gates on.
    this.mUrlTemplate = urlTemplate;

    Log.d(
      ThumbnailManager.TAG,
      "loadThumbnails(): browser params set start=" +
        String(startTime) +
        " end=" +
        String(endTime)
    );

    this.checkReadyToFetch();
  }

  /**
   * Return the blob URL of the thumbnail closest to `seekPositionSeconds`.
   *
   * On a miss this returns the last genuinely-loaded frame, not null and never
   * a raw CDN URL — null only before anything has ever loaded for the current
   * content. Handing back an unfetched URL would flash blank in the <img>.
   *
   * On 10-foot devices uses the LRU sliding-window cache.
   * On browser uses the original eager pre-fetch map.
   */
  public getThumbnail(seekPositionSeconds: number): string | null {
    if (this.m10FootIsActive && this.m10FootPlaybackCategory !== null) {
      return this.get10FootThumbnail(seekPositionSeconds);
    }
    return this.mBrowser.getThumbnail(seekPositionSeconds);
  }

  /**
   * Return the blob URL for a RAW player position (e.g. the trick-play target
   * from getTrickPlayTargetPosition() or getPlayerCurrentTime()).
   *
   * Unlike getThumbnail() — whose argument is content-relative and depends on
   * the caller computing (seekEpoch − airingStartEpoch) — this normalises the
   * raw player position through the SAME to10FootDomain() used by the prefetch
   * path (updateCurrentPosition / onSeekStarted).  That keeps the lookup and
   * the cache keys in one domain for every category, independent of the airing
   * epoch base the View layer happens to use (which is 0 for StartOver but the
   * airing epoch for CatchUp/NDVR/VOD — a mismatch that previously made every
   * VOD-category lookup miss and fall back to single on-demand fetches).
   *
   * 10-foot only; falls back to the relative-position lookup otherwise.
   */
  public getThumbnailForPlayerPosition(
    playerPositionSeconds: number
  ): string | null {
    if (this.m10FootIsActive && this.m10FootPlaybackCategory !== null) {
      return this.lookup10FootByAbsOffset(
        this.to10FootDomain(playerPositionSeconds)
      );
    }
    return this.mBrowser.getThumbnail(playerPositionSeconds);
  }

  /**
   * Subscribe to "a thumbnail blob just landed" for either strategy.
   *
   * The lookup API is deliberately synchronous and returns the last-known-good
   * frame on a miss while it fetches the real one in the background, so a
   * caller that queries once and caches the answer will show a stale frame
   * forever. This is how such a caller learns to re-query.
   *
   * @param listener - Invoked after each successful fetch.
   * @returns An unsubscribe function the caller must run on teardown.
   */
  public subscribeThumbnailAvailable(listener: () => void): () => void {
    return this.mSession.subscribe(listener);
  }

  /**
   * Snapshot of the memory diagnostics counters plus live cache state.
   *
   * Intended for the trick-play session summary log on constrained TVs. The
   * two numbers that matter:
   *
   *   blobsCreated - blobsRevoked   Outstanding blob URLs. Each pins native
   *                                 memory outside the JS heap, which is what
   *                                 the webOS / Tizen low-memory watchdog
   *                                 measures. Must return to ~0 after a
   *                                 content transition; a residue is a leak.
   *
   *   fetchesIssued /               Wasted-work ratio. The virtual seek target
   *     thumbnailsDelivered         advances only once per second at every
   *                                 speed level, so a ratio well above 1 means
   *                                 the prefetch window is running ahead of
   *                                 anything the user can actually see.
   */
  public getMemoryDiagnostics(): {
    fetchesIssued: number;
    blobsCreated: number;
    blobsRevoked: number;
    blobsOutstanding: number;
    evictions: number;
    thumbnailsDelivered: number;
    cacheSize: number;
    peakCacheSize: number;
    activeRequests: number;
    peakActiveRequests: number;
    pendingQueueDepth: number;
    inFlight: number;
  } {
    // Counters come from the session so BOTH strategies report into one pair —
    // the browser path creates and revokes object URLs too, and a split would
    // make `blobsOutstanding` useless as the leak indicator. Live cache state
    // is 10-foot only and read directly.
    const counters = this.mSession.getCounters();
    return {
      fetchesIssued: counters.fetchesIssued,
      blobsCreated: counters.blobsCreated,
      blobsRevoked: counters.blobsRevoked,
      blobsOutstanding: counters.blobsCreated - counters.blobsRevoked,
      evictions: counters.evictions,
      thumbnailsDelivered: counters.thumbnailsDelivered,
      cacheSize: this.m10FootCache.size,
      peakCacheSize: counters.peakCacheSize,
      activeRequests: this.m10FootActiveRequests,
      peakActiveRequests: counters.peakActiveRequests,
      pendingQueueDepth: this.m10FootPendingQueue.length,
      inFlight: this.m10FootInFlight.size
    };
  }

  /**
   * Emit the memory diagnostics snapshot at INFO with a caller-supplied label.
   *
   * Deliberately one line: on a TV console a multi-line dump during trick-play
   * is itself a measurable cost.
   *
   * @param context - Short label identifying the call site (e.g. "confirm").
   */
  public logMemoryDiagnostics(context: string): void {
    const d = this.getMemoryDiagnostics();
    Log.i(
      ThumbnailManager.TAG,
      "memory[" +
        context +
        "] outstandingBlobs=" +
        String(d.blobsOutstanding) +
        " created=" +
        String(d.blobsCreated) +
        " revoked=" +
        String(d.blobsRevoked) +
        " fetches=" +
        String(d.fetchesIssued) +
        " delivered=" +
        String(d.thumbnailsDelivered) +
        " evictions=" +
        String(d.evictions) +
        " cache=" +
        String(d.cacheSize) +
        "/peak" +
        String(d.peakCacheSize) +
        " active=" +
        String(d.activeRequests) +
        "/peak" +
        String(d.peakActiveRequests) +
        " pending=" +
        String(d.pendingQueueDepth)
    );
  }

  /**
   * Abort all in-flight requests and clear the thumbnail map.
   * Call on content change or player teardown.
   *
   * On 10-foot devices also clears the sliding-window cache and all 10-foot
   * timers.  The m10FootIsActive flag and m10FootConfig are preserved so the
   * strategy survives content transitions and re-initialises on the next
   * loadThumbnails().
   */
  public resetThumbnails(): void {
    // Invalidate every in-flight fetch continuation BEFORE aborting or
    // revoking anything. Aborting cannot stop a response whose body is already
    // resolving, so the token is what prevents a late callback from creating a
    // blob URL this reset can no longer revoke. Shared, so this covers the
    // browser strategy's in-flight fetches too.
    this.mSession.invalidate();

    // ── 10-foot cleanup ──────────────────────────────────────────────────
    if (this.m10FootIsActive) {
      // Abort all 10-foot in-flight fetches.
      this.m10FootInFlight.forEach((controller) => {
        controller.abort();
      });
      // Revoke blob URLs from the 10-foot cache.
      this.m10FootCache.forEach((entry) => {
        if (entry.imgBlob !== null) {
          URL.revokeObjectURL(entry.imgBlob);
          this.mSession.recordBlobRevoked();
        }
      });
      this.m10FootCache = new Map();
      this.m10FootInFlight = new Map();
      this.m10FootPendingQueue = [];
      this.m10FootPendingSet = new Set();
      this.m10FootFailedOffsets = new Map();
      this.m10FootActiveRequests = 0;
      this.m10FootPlaybackCategory = null;
      this.m10FootCurrentPosition = 0;
      this.m10FootIsFetchSuspended = false;
      this.m10FootNextLiveTime = null;
      this.clear10FootSeekSession();
      this.m10FootFetchMinOffset = null;
      this.m10FootFetchMaxOffset = null;
      this.m10FootLiveTimer = clearTimer(this.m10FootLiveTimer);
    }

    // ── Browser cleanup (ALWAYS runs) ────────────────────────────────────
    // Unconditional even in 10-foot mode: the browser strategy is the fallback
    // whenever a playback category cannot be resolved, so it may hold entries
    // and blob URLs on a TV too.
    this.mBrowser.reset();

    this.mStartTime = 0;
    this.mEndTime = 0;
    this.mUrlTemplate = "";

    // Shared debounce state (browser + 10-foot): clear the timer and reset the
    // LOADED/FIRST_FRAME latches so the next content re-arms the debounce cleanly.
    this.mIsPlayerLoaded = false;
    this.mIsFirstFrameReady = false;

    // Clear the last-known-good fallback so a stale frame from the previous
    // asset never bleeds into new content.
    this.mSession.setLastDelivered(null);
    this.mDebounceTimer = clearTimer(this.mDebounceTimer);

    // Report the balance for the content just torn down. blobsCreated and
    // blobsRevoked stay cumulative so `outstandingBlobs` remains a true running
    // total across content transitions — it is the leak indicator and should
    // read ~0 here. Only the per-content peaks are cleared.
    this.logMemoryDiagnostics("reset");
    this.mSession.resetPeaks();

    Log.i(ThumbnailManager.TAG, "resetThumbnails(): cleared");
  }
}
